"use client";

import { useLayoutEffect, useRef, useState, type RefObject } from "react";
import {
    SCENE_BASE_WIDTH,
    SCENE_BASE_HEIGHT,
} from "@/features/lomdaEditorV2/lib/sceneConstants";

const SCENE_ASPECT_RATIO = 16 / 9;

export interface SceneFit {
    width: number;
    height: number;
}

export function useSceneFit<T extends HTMLElement = HTMLDivElement>(): {
    ref: RefObject<T>;
    fit: SceneFit | null;
} {
    const ref = useRef<T>(null);
    const [fit, setFit] = useState<SceneFit | null>(null);

    useLayoutEffect(() => {
        const el = ref.current;
        if (!el) return;

        const update = () => {
            const { width, height } = el.getBoundingClientRect();
            if (!width || !height) return;
            const fitWidth = Math.min(width, height * SCENE_ASPECT_RATIO);
            const fitHeight = fitWidth / SCENE_ASPECT_RATIO;
            const scale = Math.min(
                fitWidth / SCENE_BASE_WIDTH,
                fitHeight / SCENE_BASE_HEIGHT
            );
            el.style.setProperty("--scene-fit-width", `${fitWidth}px`);
            el.style.setProperty("--scene-fit-height", `${fitHeight}px`);
            el.style.setProperty("--scene-font-scale", String(scale));
            setFit({ width: fitWidth, height: fitHeight });
        };

        update();
        const observer = new ResizeObserver(update);
        observer.observe(el);
        return () => observer.disconnect();
    }, []);

    return { ref, fit };
}
